import { Condition, ConditionType, conditionTypeIndex, ConditionTypes } from "@types-runway/condition";
import {
  FlattenedPage,
  GENERIC_SECTIONS,
  Option,
  Page,
  PageCategory,
  Pages,
  QuestionnairePages,
} from "@types-runway/questionnaire";
import { uniqueConditions } from "@utils/case/uniqueConditions";
import USStates from "@utils/forms/USstates.json";

const formatSectionTitle = (section: string): string => {
  return section
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const flattenQuestionnairePages = (
  content: QuestionnairePages,
  selectedConditions: Condition[],
  isPlusFlow: boolean
): FlattenedPage[] => {
  const questionnairePages = content;

  // Load US states into the questionnaire.
  const USStatesIndex = questionnairePages.user.pages.findIndex(
    (page) => page.stateKey === "stateOfResidence"
  );
  if (USStatesIndex !== -1) {
    questionnairePages.user.pages[USStatesIndex].options = USStates.map((state) => ({
      title: state.name,
    }));
  }

  const result: FlattenedPage[] = [];

  const addPageToResult = (page: Page, condition?: ConditionType) => {
    const isMergeablePage =
      page.stateKey === "immunocompromised" ||
      ["otherConditions", "medicationAllergies"].includes(page.stateKey);

    if ((page.inputType.input === "checkbox" || isMergeablePage) && page.options) {
      const existingPageIndex = result.findIndex((p) => p.stateKey === page.stateKey);

      if (existingPageIndex !== -1) {
        const existingPage = result[existingPageIndex];

        const isAListPage =
          page.inputType.input === "checkbox" || page.inputType.input === "radio";
        let options = page.options ?? [];

        if (isAListPage) {
          const uniqueOptionsMap = new Map<string, Option>();

          const addOptionsToMap = (opts: Option[]) => {
            opts.forEach((option) => {
              if (!uniqueOptionsMap.has(option.title)) {
                uniqueOptionsMap.set(option.title, option);
              }
            });
          };

          addOptionsToMap(existingPage.options ?? []);
          addOptionsToMap(page.options ?? []);

          options = Array.from(uniqueOptionsMap.values()).filter(
            (option) =>
              option.title.toLocaleLowerCase() !== "yes" &&
              option.title.toLocaleLowerCase() !== "no"
          );
        }

        const mergedFrom = existingPage.mergedFrom || [];
        if (condition && !mergedFrom.includes(condition)) {
          mergedFrom.push(condition);
        }

        result[existingPageIndex] = {
          ...existingPage,
          inputType: page.inputType,
          options: Array.from(new Set(options)).sort(),
          mergedFrom: mergedFrom,
        };
      } else {
        result.push({
          ...page,
          section: "conditions" as PageCategory,
          sectionTitle: "Conditions",
          conditionType: condition,
          stateKey: page.stateKey,
          parentCondition: condition,
          mergedFrom: condition ? [condition] : [],
          options: (page.options ?? []).sort(),
        });
      }
    } else {
      result.push({
        ...page,
        section: "conditions" as PageCategory,
        sectionTitle: "Conditions",
        conditionType: condition,
        stateKey: page.stateKey,
        parentCondition: condition,
      });
    }
  };

  const addGenericPages = (section: PageCategory, sectionPages: Page[]) => {
    if (!sectionPages) return;
    sectionPages.forEach((page) => {
      result.push({
        ...page,
        section,
        sectionTitle: formatSectionTitle(section),
      });
    });
  };

  // Add pages from generic sections
  GENERIC_SECTIONS.forEach((section: PageCategory) => {
    addGenericPages(section, (questionnairePages[section] as Pages).pages);
  });

  // Process condition pages
  const conditionNames = uniqueConditions(selectedConditions).map(
    (condition) => condition.product.name as ConditionTypes
  );

  if (isPlusFlow) {
    conditionNames.push(ConditionTypes.RUNWAY_PLUS);
  }

  conditionNames.forEach((conditionName) => {
    const conditionType = conditionTypeIndex.get(conditionName) ?? "plus";
    const conditionContent = questionnairePages.conditions[conditionType];
    if (!conditionContent) return;

    const { pages } = conditionContent;

    pages.forEach((page) => {
      addPageToResult(page, conditionType);
    });
  });

  // Add remaining sections
  ["marketing", "consent"].forEach((section) => {
    const sectionData = questionnairePages[section as PageCategory] as Pages;
    if (sectionData?.pages) {
      addGenericPages(section as PageCategory, sectionData.pages);
    }
  });

  return result;
};
